import { Show, createSignal, onMount } from "solid-js";
import type { AuthModule, AuthResult, LoginAudience } from "./auth";
import { Icon } from "~/ui/Icon";

export function LoginPage(props: { auth: AuthModule; onAuthenticated?: (result: AuthResult, audience: LoginAudience) => void }) {
  const [audience, setAudience] = createSignal<LoginAudience>(props.auth.loginAudience());
  const [busy, setBusy] = createSignal(false);
  const [error, setError] = createSignal("");
  const [showTeacherPw, setShowTeacherPw] = createSignal(false);
  const [showStudentPw, setShowStudentPw] = createSignal(false);
  let teacherEmail!: HTMLInputElement;
  let teacherPassword!: HTMLInputElement;
  let teacherRemember!: HTMLInputElement;
  let studentCode!: HTMLInputElement;
  let studentName!: HTMLInputElement;
  let studentPassword!: HTMLInputElement;
  let studentRemember!: HTMLInputElement;

  onMount(() => {
    document.body.className = "";
    const savedTeacher = props.auth.savedTeacher();
    if (savedTeacher) {
      teacherEmail.value = savedTeacher.email;
      teacherRemember.checked = true;
    }
    const savedStudent = props.auth.savedStudent();
    if (savedStudent) {
      studentCode.value = savedStudent.code;
      studentName.value = savedStudent.name;
      studentRemember.checked = true;
    }
  });

  const choose = (value: LoginAudience) => {
    setAudience(value);
    setError("");
    props.auth.rememberLoginAudience(value);
  };

  const run = async (action: () => Promise<AuthResult>) => {
    setBusy(true);
    setError("");
    try { props.onAuthenticated?.(await action(), audience()); }
    catch (cause) { setError(cause instanceof Error ? cause.message : "Anmeldung fehlgeschlagen"); }
    finally { setBusy(false); }
  };

  const teacherLogin = (event: SubmitEvent) => {
    event.preventDefault();
    const email = teacherEmail.value.trim();
    const password = teacherPassword.value;
    if (!email || !password) return setError("Bitte E-Mail und Passwort eingeben");
    void run(() => props.auth.loginTeacher({ email, password, remember: teacherRemember.checked }));
  };
  const studentLogin = (event: SubmitEvent) => {
    event.preventDefault();
    const code = studentCode.value.trim().toUpperCase();
    const name = studentName.value.trim();
    const password = studentPassword.value;
    if (!code || !name || !password) return setError("Bitte alle Felder ausfüllen");
    void run(() => props.auth.loginStudent({ code, name, password, remember: studentRemember.checked }));
  };

  return (
    <div class="auth-container">
      <div class="deco-blob auth-blob-1" /><div class="deco-blob auth-blob-2" /><div class="deco-blob auth-blob-3" />

      <div class="auth-hero"><img class="auth-hero-logo" src="/img/reflexo-logo.png" alt="Reflexo" /></div>

      <div class="auth-card fade-in">
        <div class="auth-tabs" role="tablist" aria-label="Anmeldung auswählen">
          <button type="button" class={`auth-tab ${audience() === "student" ? "active" : ""}`} role="tab" aria-selected={audience() === "student"} onClick={() => choose("student")}><Icon name="backpack" /> Schüler</button>
          <button type="button" class={`auth-tab ${audience() === "teacher" ? "active" : ""}`} role="tab" aria-selected={audience() === "teacher"} onClick={() => choose("teacher")}><Icon name="teacher" /> Lehrkraft</button>
          <button type="button" class={`auth-tab ${audience() === "principal" ? "active" : ""}`} role="tab" aria-selected={audience() === "principal"} onClick={() => choose("principal")}><Icon name="school" /> Schulleitung</button>
        </div>

        <Show when={audience() !== "student"} fallback={
          <form aria-label="Schüler-Login" onSubmit={studentLogin}>
            <p style={{ "margin-bottom": "20px", "text-align": "center", "font-size": "0.9rem" }}>Gib deinen Klassen-Code und deinen Vornamen ein</p>
            <div class="form-group"><label class="form-label" for="student-code">Klassen-Code</label><input ref={studentCode} type="text" id="student-code" class="form-input" placeholder="z.B. ABCD1234" style={{ "text-transform": "uppercase", "font-size": "1.3rem", "text-align": "center", "letter-spacing": "4px" }} maxlength={8} /></div>
            <div class="form-group"><label class="form-label" for="student-name">Dein Vorname</label><input ref={studentName} type="text" id="student-name" class="form-input" placeholder="z.B. Felix" /></div>
            <div class="form-group">
              <label class="form-label" for="student-password">Passwort</label>
              <div class="pw-field">
                <input ref={studentPassword} type={showStudentPw() ? "text" : "password"} id="student-password" class="form-input" placeholder="••••" autocomplete="current-password" />
                <button type="button" class="pw-toggle" aria-label={showStudentPw() ? "Passwort verbergen" : "Passwort anzeigen"} onClick={() => setShowStudentPw(v => !v)}>
                  <Icon name={showStudentPw() ? "eyeOff" : "eye"} />
                </button>
              </div>
            </div>
            <label style={{ display: "flex", "align-items": "center", gap: "8px", "margin-bottom": "16px", cursor: "pointer", "font-size": "0.85rem", color: "var(--text-muted)" }}><input ref={studentRemember} type="checkbox" style={{ width: "16px", height: "16px", cursor: "pointer" }} /> Auf diesem Gerät merken</label>
            <button class="btn btn-primary btn-full btn-lg" type="submit" disabled={busy()}>Los geht's! 🚀</button>
            <div style={{ "margin-top": "16px", "text-align": "center" }}><small style={{ color: "var(--text-muted)" }}>Neu? Frag deinen Lehrer nach dem Klassen-Code und deinem Passwort.</small></div>
          </form>
        }>
          <form onSubmit={teacherLogin}>
            <div class="form-group"><label class="form-label" for="login-email">E-Mail</label><input ref={teacherEmail} type="email" id="login-email" class="form-input" placeholder="lehrer@schule.de" /></div>
            <div class="form-group">
              <label class="form-label" for="login-password">Passwort</label>
              <div class="pw-field">
                <input ref={teacherPassword} type={showTeacherPw() ? "text" : "password"} id="login-password" class="form-input" placeholder="••••••••" autocomplete="current-password" />
                <button type="button" class="pw-toggle" aria-label={showTeacherPw() ? "Passwort verbergen" : "Passwort anzeigen"} onClick={() => setShowTeacherPw(v => !v)}>
                  <Icon name={showTeacherPw() ? "eyeOff" : "eye"} />
                </button>
              </div>
            </div>
            <label style={{ display: "flex", "align-items": "center", gap: "8px", "margin-bottom": "16px", cursor: "pointer", "font-size": "0.85rem", color: "var(--text-muted)" }}><input ref={teacherRemember} type="checkbox" style={{ width: "16px", height: "16px", cursor: "pointer" }} /> Auf diesem Gerät merken</label>
            <button class="btn btn-primary btn-full btn-lg" type="submit" disabled={busy()}>Einloggen →</button>
            <Show when={audience() === "teacher"} fallback={
              <div style={{ "margin-top": "16px", "text-align": "center" }}><small style={{ color: "var(--text-muted)" }}>Noch nicht registriert? <a href="/schule" style={{ color: "var(--primary)", "font-weight": "800" }}>Schule registrieren</a></small></div>
            }>
              <div style={{ "margin-top": "16px", "text-align": "center" }}><small style={{ color: "var(--text-muted)" }}>Noch kein Zugang? Ihre Schulleitung lädt Sie per E-Mail ein. Der Link in der Mail führt Sie direkt zur Anmeldung.</small></div>
            </Show>
          </form>
        </Show>
        <Show when={error()}><div class="form-error" style={{ "margin-top": "12px", "text-align": "center" }} role="alert">{error()}</div></Show>
      </div>
    </div>
  );
}
